import ContentConfig from '../config/ContentConfig';
import supportsUnsecureLocalhost from '../common/utils/supportsUnsecureLocalhost';
import racedFetch from '../common/utils/racedFetch';
import xhr from '../common/utils/xhr';
import { SourceType, noop } from '../common/constants';
import logger from '../log';

const log = logger.getLogger(__filename);
const contentConfig = ContentConfig.getInstance();

export const TIMEOUT_DEFAULT = 2000;
const TIMEOUT_PREFETCH = 5000;
const TIMEOUT_LONGPOLL = 30000;
export const URI_DETECTION = 'kontiki/kontiki/cache/RetrieveAgentData';
export const URI_DOWNLOAD_MONITOR = 'api/v1/downloadMonitor';
const URI_SESSION = 'api/v1/setSessionData';
const URI_DELIVERY_GUID = 'api/v1/contentDeliveryGuid';
const URI_STOP_STREAM = 'api/v1/stopStream';
const URI_LONGPOLL = 'api/v1/longpoll';
const URI_PREF_LIST = 'api/v1/getPrefList';
const URI_PREF = 'api/v1/getPref';

function createStreamURI(urn, sourceType) {
  const filename = (() => {
    switch (sourceType) {
      case SourceType.MP4:
        return 'content.mp4';
      case SourceType.HLS:
        return 'chunklist.m3u8';
      case SourceType.DASH:
        return 'playlist.ism/Manifest(format=mpd-time-csf)';
      case SourceType.SMOOTH:
        return 'playlist.ism/Manifest';
      default:
        return '';
    }
  })();
  return `stream/${urn}/${filename}`;
}

function createURL(config, uri) {
  const { protocol, host, port } = config;
  return `${protocol}://${host}:${port}/${uri}`;
}

function checkConfig() {
  if (!this.config) {
    // This is due to detect() not having been successfully called first
    throw Error('ECDN10AgentAPI lacks config');
  }
}

/*
 *  The `detect()`method must be called before any other ECDN 10 Agent API calls, as agent preferences
 *  (potentially) returned in the call are necessary for branching logic elsewhere. This is expected
 *  even if detection had already happened on another lobby page as well.
 */
export class ECDN10AgentAPI {
  // The `detect()` method is special:
  //   - It must be called before other methods
  //   - It attempts the request over HTTPS and then, if the browser allows, over HTTP
  //   - ECDN10AgentAPI remembers the config (protocol, host, port) that successfully reached the agent, if any
  //   - There is just one `callback` option (not `onSuccess`, etc), which will receive info about all detection request attempts made
  detect(options) {
    /* eslint-disable prefer-const */
    let { callback } = options || {};
    callback = callback || noop;
    log.info('ECDN10AgentAPI:detect:', { options });
    const configs = this.detectConfigs();

    // failures: List of failed detection attempts (potentially one for each config)
    const requestWithNextConfig = (failures = []) => {
      if (!configs.length) {
        callback({ failures });
        return;
      }
      const config = configs.shift();
      const start = Date.now();

      const onSuccess = (data, request) => {
        this.config = config;
        log.debug('ecdn10AgentAPI success', { config });
        callback({
          success: { protocol: config.protocol, data, request, start },
          failures
        });
      };

      const onFailure = request => {
        failures.push({
          start,
          t: Date.now(),
          protocol: config.protocol,
          request
        });
        log.debug('ecdn10AgentAPI failed', { config });
        requestWithNextConfig(failures);
      };

      this.makeRequest(
        {
          uri: URI_DETECTION,
          onSuccess,
          onFailure,
          onTimeout: onFailure
        },
        config
      );
    };
    requestWithNextConfig();
  }

  detectConfigs() {
    const { a: host, p: port } = contentConfig;
    const useInsecure = !host && !port && supportsUnsecureLocalhost();
    const secureConfig = {
      protocol: 'https',
      host: host || 'client.kontiki.com',
      port: port || 31015
    };
    const unsecureConfig = {
      protocol: 'http',
      host: '127.0.0.1',
      port: 31013
    };
    // Use determined config, if already known
    return this.config
      ? [this.config]
      : [secureConfig, useInsecure && unsecureConfig].filter(Boolean);
  }

  async initialize() {
    if (this.config && this.configDetection) {
      return this.configDetection;
    }
    // racedFetch each config
    const configs = this.detectConfigs();
    this.configDetection = { failures: [] };
    for (let i = 0; i < configs.length; ++i) {
      const config = configs[i];
      const start = Date.now();
      const { protocol } = config;
      const args = {
        retries: 0,
        timeout: TIMEOUT_DEFAULT,
        url: createURL(config, URI_DETECTION)
      };
      const [success, result] = await racedFetch(args)
        .then(request => {
          return [true, { request, start, t: Date.now() }];
        })
        .catch(request => {
          return [false, { protocol, request, start, t: Date.now() }];
        });
      if (success) {
        this.config = config;
        const data = await result.request.json();
        this.configDetection.success = { ...result, ...config, data };
        return this.configDetection;
      }
      this.configDetection.failures.push(result);
    }
    return this.configDetection;
  }

  async gatherDownloadMonitorInfo(config) {
    if (!config) {
      checkConfig.call(this);
    }
    const args = {
      retries: 2,
      timeout: TIMEOUT_DEFAULT,
      url: createURL(config || this.config, URI_DOWNLOAD_MONITOR)
    };
    return await racedFetch(args);
  }

  startSession(options) {
    const {
      body: { ktoken, appContext }
    } = options;
    this.makeRequest({
      method: 'POST',
      uri: `${URI_SESSION}?ktoken=${ktoken}&appContext=${appContext}&nopreroll=${!options.usePreRoll}`,
      ...options
    });
  }

  getDownloadMonitorInfo(options) {
    log.debug('getDownloadMonitorInfo:', options);
    this.makeRequest({
      uri: `${URI_DOWNLOAD_MONITOR}`,
      ...options
    });
  }

  getDeliveryGuid(options) {
    const urn = options.urn;
    const uri = `${URI_DELIVERY_GUID}?urn=${urn}`;
    log.debug('ECDN10AgentAPI:getDeliveryGuid:', { uri, urn, options });
    delete options.urn;
    this.makeRequest({ uri, ...options });
  }

  getPrefList(options) {
    const uri = URI_PREF_LIST;
    log.debug('ECDN10AgentAPI:getDeliveryGuid:', { uri, options });
    this.makeRequest({ uri, ...options });
  }

  getPref(options) {
    const { name } = options;
    const uri = `${URI_PREF}?name=${name}`;
    log.debug('ECDN10AgentAPI:getDeliveryGuid:', { uri, options });
    delete options.name;
    this.makeRequest({ uri, ...options });
  }

  prefetch(options) {
    const { urn, streamType, ...opts } = options;
    this.makeRequest({
      uri: createStreamURI(urn, streamType),
      timeout: TIMEOUT_PREFETCH,
      expectJson: false,
      ...opts
    });
  }

  longpoll(options) {
    this.makeRequest({
      uri: URI_LONGPOLL,
      timeout: TIMEOUT_LONGPOLL,
      ignoreEdgeTimeout: true,
      ...options
    });
  }

  stopStream(options) {
    this.makeRequest({
      uri: URI_STOP_STREAM,
      ...options
    });
  }

  makeRequest(options, config) {
    if (!config) {
      checkConfig.call(this);
    }
    const { uri, timeout, ...opts } = options || {};
    const url = createURL(config || this.config, uri);
    const thisConfig = this.config;
    log.debug('ecdn10:AgentAPI.makeRequest:', {
      options,
      url,
      thisConfig,
      config
    });
    return xhr({
      url: createURL(config || this.config, uri),
      timeout: timeout || TIMEOUT_DEFAULT,
      jsonContent: false,
      ...opts
    });
  }

  createStreamURL(urn, streamType) {
    checkConfig.call(this);
    const uri = createStreamURI(urn, streamType);
    return createURL(this.config, uri);
  }
}

const ecdn10AgentAPI = new ECDN10AgentAPI();

export default ecdn10AgentAPI;
